import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { effectiveModules } from "@app/shared";
import {
  ChevronLeft,
  Clapperboard,
  Images,
  LayoutList,
  Loader2,
  LogOut,
  Megaphone,
  Newspaper,
  Share2,
  Sparkles,
  Wand2,
} from "lucide-react";
import { api } from "../lib/api";
import { cn } from "../lib/cn";
import { useBrands } from "../lib/brand";
import { useLogout, useSessionUser } from "../lib/session";
import { BrandSwitcher } from "./BrandSwitcher";
import { Badge } from "./ui/badge";
import { Brandmark } from "./Brandmark";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutList;
  /** Brand-module key — the nav item shows only when the active brand enables it. */
  module: string;
}

/** Workspace = the per-brand content pages (operate on the active brand). */
const NAV_ITEMS: NavItem[] = [
  { to: "/drafts", label: "Reguler", icon: LayoutList, module: "reguler" },
  { to: "/special", label: "Special Case", icon: Sparkles, module: "special" },
  { to: "/articles", label: "Artikel", icon: Newspaper, module: "article" },
  { to: "/custom", label: "Custom", icon: Wand2, module: "custom" },
  { to: "/media", label: "Galeri", icon: Images, module: "media" },
  { to: "/materials", label: "Materi", icon: Megaphone, module: "material" },
  { to: "/videos", label: "Video", icon: Clapperboard, module: "video" },
];

/**
 * The brand WORKSPACE shell: a top nav of content pages scoped to the active
 * brand. Admins reach it by "opening" a brand from the Console and can return via
 * "← Console"; brand/manajer users live here directly (with the brand switcher).
 */
export function WorkspaceLayout() {
  const user = useSessionUser();
  const { isAdmin, active } = useBrands();
  const location = useLocation();
  const queryClient = useQueryClient();
  const logout = useLogout();

  // Poll background generation activity for the "generating now" indicator.
  const statusQuery = useQuery({
    queryKey: ["generateStatus"],
    queryFn: ({ signal }) => api.generateStatus(signal),
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  });
  const busy = statusQuery.data?.busy ?? false;
  const busyCount =
    (statusQuery.data?.active ?? 0) + (statusQuery.data?.waiting ?? 0);

  const wasBusy = useRef(false);
  useEffect(() => {
    if (wasBusy.current && !busy) {
      void queryClient.invalidateQueries({ queryKey: ["drafts"] });
      void queryClient.invalidateQueries({ queryKey: ["highlights"] });
      void queryClient.invalidateQueries({ queryKey: ["articles"] });
      void queryClient.invalidateQueries({ queryKey: ["materials"] });
      void queryClient.invalidateQueries({ queryKey: ["videos"] });
    }
    wasBusy.current = busy;
  }, [busy, queryClient]);

  // The active brand's enabled menus drive the workspace nav (empty ⇒ all).
  const enabled = effectiveModules(active?.modules);
  const navItems = NAV_ITEMS.filter((i) => enabled.includes(i.module));
  const current = NAV_ITEMS.find(
    (i) =>
      location.pathname === i.to || location.pathname.startsWith(`${i.to}/`),
  );
  // Direct URL to a module the brand has disabled → bounce to the first enabled.
  if (current && !enabled.includes(current.module) && navItems[0]) {
    return <Navigate to={navItems[0].to} replace />;
  }

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-navy text-white">
        <div className="mx-auto max-w-6xl px-4">
          {/* Row 1: identity + account controls */}
          <div className="flex h-14 items-center justify-between gap-4">
            {isAdmin ? (
              <Link
                to="/dashboard"
                className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <ChevronLeft className="h-4 w-4" />
                Console
              </Link>
            ) : (
              <Brandmark variant="header" />
            )}
            <div className="flex shrink-0 items-center gap-2">
              <BrandSwitcher />
              <Badge variant="teal" className="hidden sm:inline-flex">
                {user.role}
              </Badge>
              <button
                type="button"
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
                aria-label="Logout"
                className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-white/70 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
          {/* Row 2: content nav — own full-width row, wraps so nothing is cut off */}
          <nav className="flex flex-wrap items-center gap-1 pb-2.5">
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-white/15 text-white"
                      : "text-white/70 hover:bg-white/10 hover:text-white",
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
            <NavLink
              to="/connections"
              className={({ isActive }) =>
                cn(
                  "flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-white/15 text-white"
                    : "text-white/70 hover:bg-white/10 hover:text-white",
                )
              }
            >
              <Share2 className="h-4 w-4" />
              Koneksi
            </NavLink>
          </nav>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
        <Outlet />
      </main>

      {busy ? (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-navy/95 px-4 py-2 text-sm font-medium text-white shadow-xl ring-1 ring-teal/40 backdrop-blur"
        >
          <Loader2 className="h-4 w-4 animate-spin text-teal" />
          Sedang generate{busyCount > 1 ? ` (${busyCount})` : ""}…
        </div>
      ) : null}
    </div>
  );
}
